/**
 * Licensed Materials - Property of IBM
 *
 * "Restricted Materials of IBM"
 *
 * (C) Copyright IBM Corp. 2018, 2020
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import * as mobx from 'mobx';
import { observer } from 'mobx-react';
import { destroy } from 'mobx-state-tree';
import { PENDING, REJECTED, FULFILLED } from 'mobx-utils';

import PropTypes from 'util/PropTypes';
import classNames from 'classnames';
import Measure from 'react-measure';


const _ = require('underscore');
import { Slider, ToggleSwitch, Tooltip } from '@ba/ba-ui-carbon-toolkit';

import RelationshipWidgetViewModel from './RelationshipWidgetViewModel';
import DndHelper from './RelationshipDnDHelper';

import RelationshipStrength from './RelationshipStrength';
import StringResources from '../../../nls/StringResources';
import './relationshipVisView.scss';

const VIS_CONTAINER_DOM_ID = 'relationship_graph';
const RENDER_MODE_CLIENT = 'client';
const NODESLIMIT_MIN = 2;
const NODESLIMIT_MAX = 50;
const DEFAULT_NODES_LIMIT = 10;

import RelationshipToolbar from './RelationshipToolbar';
import OverrideList from './OverrideList';
import { SVGIcon } from '@ba/ba-ui-carbon-toolkit';
import filter16 from '@ba/ba-graphics/dist/icons-js/filter_16';
import { OVERRIDE_ACTIONS } from './RelationshipTargetModel';

@observer
class RelationshipWidgetView  extends Component {
	/***
	 *@constructor
	 **/
	constructor(props) {
		super(props);
		this.hintRef = React.createRef();//DOM ref to hint text DIV
		this.measureRef = React.createRef();//DOM ref to the React Measure component which holds the relationship diagram
		this.state = { dndHover: false };
		if (!this.props.relationship.getIsFilterStrengthFeatureEnabled()) {
			this.state.slider = DEFAULT_NODES_LIMIT;
		}
		this.model = RelationshipWidgetViewModel.create();
		this.disposeFunctions = [];


		//TODO Move to RelationshipWidgetViewModel per https://github.ibm.com/BusinessAnalytics/explore/pull/576#discussion-diff-1115614R40
		this.disposeFunctions.push(
			mobx.autorun(() => {
				const targetModel = this.props.relationship.getTargetModel();
				const state = targetModel && targetModel.data && targetModel.data.state;
				if(state === FULFILLED) {
					this.model.setLastVisValid(true);
					//TODO: Render error without wiping away previous diagram
				} else if(state === REJECTED ) {
					this.model.setLastVisValid(false);
				} else if(state === PENDING) {
					var eventHandler = this.getEventHandler && this.getEventHandler();
					if(eventHandler) {
						eventHandler.onDestroy();
					}
				}
			})
		);

		this.disposeFunctions.push(
			mobx.reaction(() => {
				return this.props.relationship.nodesLimit;
			}, nodesLimit => {
				this.setState({ slider: nodesLimit });
			})
		);

		this.disposeFunctions.push(
			mobx.autorun(() => {
				if(this.props.relationship.contextMenuField && this.props.relationship.contextMenuElement) {
					this.model.setContextMenuTarget(this.props.relationship.contextMenuElement);
				} else {
					this.model.setContextMenuTarget(null);
				}
			})
		);

	}


	/* Hookup render DOM with vis loader */
	componentDidMount() {
		const measureRefNode = this._getMeasureDOMNode();
		if(measureRefNode){
			measureRefNode.addEventListener('mouseenter', this._onMouseEnter.bind(this));
			measureRefNode.addEventListener('mouseleave', this._onMouseLeave.bind(this));
		}
		if(this.props.relationship.isAddFieldFeatureEnabled()){
			this._initializeDndSupport();
		}
		//TODO: revisit this that we may want to return a handler module from visLoader
		this.props.relationship.setElement(this.el)
			.then(({ getEventHandler, widget, addHighlight, removeHighlight }) => {
				this.getEventHandler = getEventHandler;
				this.widget = widget;
				this.addHighlight = addHighlight; /*get the node that needs to add hightlight*/
				this.removeHighlight = removeHighlight; /*get the node that needs to remove hightlight*/
				this._onScrollhandler = this._onScroll.bind(this);

				window.addEventListener('scroll', this._onScrollhandler, true);
			});
	}

	_initializeDndSupport() {
		this.props.services && this.props.services.getSvc('.DndManager').then(
			(dndManager) => {
				if (this.el) {
					this._dndManager = dndManager;
					this.dropOnDiagram = new DndHelper(
						this.el,
						'dndHover',
						this._handleDrop.bind(this),
						this._handleAccept.bind(this),
						this._handleDragEnter.bind(this),
						this._handleDragLeave.bind(this),
						this.props.relationship,
					);
					this.dropOnDiagram.initializeDndSource(this._dndManager, this._callback.bind(this));
					dndManager.addDropTarget(this.el, this.dropOnDiagram);
				}
			}
		);
	}


	componentWillUnmount() {
		this.disposeFunctions.forEach(disposeFunction => disposeFunction());
		this.disposeFunctions = null;
		const measureRefNode = this._getMeasureDOMNode();
		if(measureRefNode){
			measureRefNode.removeEventListener('mouseenter', this._onMouseEnter);
			measureRefNode.removeEventListener('mouseleave', this._onMouseLeave);
		}
		if (this._onScrollhandler) {
			window.removeEventListener('scroll', this._onScrollhandler, true);
			this._onScrollhandler = null;
		}
		if(this.model) {
			destroy(this.model);
			this.model = null;
		}
	}

	_getMeasureDOMNode(){
		if(this.measureRef && this.measureRef.current){
			return this.measureRef.current._node;//workaround to return Measure componnet's DOM Node
		}
		return null;
	}

	_onScroll() {
		const eventHandler = this._getEventHandler();
		return eventHandler && eventHandler.removeTooltip();
	}

	/* Render the widget visView */
	render() {
		return this._buildRenderMarkup();
	}

	_buildRenderMarkup(){
		const targetModel = this.props.relationship.getTargetModel();
		const state = targetModel && targetModel.data && targetModel.data.state;
		const error = state === REJECTED;
		const pending = state === PENDING;
		const sVizContainerID = _.uniqueId(VIS_CONTAINER_DOM_ID);
		this.sVizContainerID = sVizContainerID;
		const widgetParentClasses = classNames({
			'relationship-vis-view__widget-parent-placeholder' : true,
			'relationship-vis-view__widget-parent-placeholder--error' : error && !this.model.lastVisValid,
			'relationship-vis-view__widget-parent-placeholder--loading' : pending,
			'relationship-vis-view__widget-parent-placeholder--loading-error' : pending && !this.model.lastVisValid,
			'relationship-vis-view__widget-parent-placeholder--loading-ok' : pending && this.model.lastVisValid
		});
		const visContainerParentClasses = classNames({
			'relationship-vis-view__widget-parent-placeholder_containerDndHover' : this.state.dndHover,
			'relationship-vis-view__widget-parent-placeholder__widget_wrapper': true
		});
		const visContainerClasses = classNames({
			'relationship-vis-view__widget-parent-placeholder__widget': true
		});
		const filterIcon = (this.props.relationship.isFilterStartingPointsEnabled() && this.props.relationship.getFilterCount() > 0) ? (
			<div className="relationship-vis-view__filter">
				<SVGIcon iconId={filter16.id} intent="primary" />
			</div>
		) : null;
		return(
			<Measure ref={this.measureRef}
				onResize={() => this.onResize() }
			>
				{({ measureRef }) =>
					<div className={widgetParentClasses} ref={measureRef}>
						{this._buildToolbarMarkup(state)}
						{this._buildRelationshipToolbar()}
						<div id={sVizContainerID + '_parent'} className ={visContainerParentClasses}>
							{this.props.relationship.isOverrideListFeatureEnabled() ? this._renderOverrideList() : null}
							{filterIcon}
							<div
								id={sVizContainerID}
								className={visContainerClasses}
								ref={el => this.el = el}
								onClick={(event) => this._onClick(event)}
								onContextMenu={(event) => this._onContextMenu(event)}
								onMouseOver={(event) => this._onMouseOver(event)}
								onMouseOut={(event) => this._onMouseOut(event)}
								onTouchStart={(event) => this._onTouchStart(event)}
								onTouchEnd={(event) => this._onTouchEnd(event)}
								onKeyDown={(event) => this._onKeyDown(event)}
							></div>
							<div className ='starting-points-page__relationship__hint' ref={this.hintRef}>
								{StringResources.get('startingPoints_select_diagram_node_hints')}
							</div>
							{this._buildRelationshipStrength()}
						</div>
					</div>
				}
			</Measure>
		);
	}


	_renderOverrideList() {
		const targetModel = this.props.relationship.getTargetModel();
		let overrideList = null;
		if(targetModel) {
			const addedFields = targetModel.getAddedFields();
			const deletedFields = targetModel.getDeletedFields();
			const allFields = this.props.fields.getFields().getAll();
			const mapOverrideFields = (action, fieldID) => {
				const foundField = allFields.find(field => field.id === fieldID);
				return {
					fieldLabel: foundField.name,
					fieldID,
					action
				};
			};
			const overrideFields = [
				...(addedFields.map((addedField) => mapOverrideFields(OVERRIDE_ACTIONS.ADDED, addedField)).reverse()),
				...(deletedFields.map((deletedField) => mapOverrideFields(OVERRIDE_ACTIONS.DELETED, deletedField)).reverse())
			];
			if(overrideFields.length) {
				overrideList = (
					<OverrideList
						overrideFields={overrideFields}
						cancelAction={(actionToCancel, fieldID) => {
							// Do cancel action
							targetModel.cancelAction(actionToCancel, fieldID);
						}}
					/>
				);
			}
		}
		return overrideList;
	}

	_buildRelationshipToolbar() {
		if (this.props.relationship.getIsFilterStrengthFeatureEnabled() && !this.props.relationship.isNewStartingPointsFeatureEnabled()) {
			const targetModel = this.props.relationship.getTargetModel();
			// Using void to force the slider to update when min/max strength is loaded
			if (targetModel) {
				void(targetModel.initialFilterStrength.min);
				void(targetModel.initialFilterStrength.max);
				return (
					<RelationshipToolbar targetModel={targetModel}/>
				);
			} else {
				return null;
			}
		} else {
			return null;
		}
	}

	_buildToolbarMarkup(state){
		if (this.props.relationship.getIsFilterStrengthFeatureEnabled() || this.props.relationship.isNewStartingPointsFeatureEnabled()) {
			return null;
		}
		return (
			<div className = "relationship-vis-view__toolbar">
				{this._buildToggleMarkUp(state)}
				{this._buildSliderMarkup(state)}
			</div>
		);
	}

	_buildRelationshipStrength(){
		if (!this.props.relationship.isNewStartingPointsFeatureEnabled()) {
			return null;
		}
		const targetModel = this.props.relationship.getTargetModel();
		if(!targetModel){
			return null;
		}
		// Using void to force the slider to update when min/max strength is loaded
		void(targetModel.initialMinStrength);
		void(targetModel.initialMaxStrength);
		return (
			<RelationshipStrength targetModel={targetModel}/>
		);
	}

	_buildToggleMarkUp() {
		if(!this.props.relationship.isConceptsViewEnabled()){
			return null;
		}
		const toggleLabel = StringResources.get('relationshipGraph_toggle_label');
		const alignment = 'right';
		return (
			<div className = "relationship-vis-view__toggler">
				<ToggleSwitch
					label = {toggleLabel}
					align = {alignment}
					onChange={() => {
						this.props.relationship.setShowConcepts(!this.props.relationship.showConcepts);
					}}
					checked={this.props.relationship.showConcepts}
					small
				/>
			</div>
		);
	}

	_buildSliderMarkup() {
		const classes = classNames({
			'relationship-vis-view__sliderParent' : true
		});
		const sliderLabel = StringResources.get('relationshipGraph_slider_label');
		const sliderTooltip = StringResources.get('relationshipGraph_slider_tooltip');

		return (<div className = {classes}
			onMouseUp ={()=>this._sliderOnMouseUp()}
			onKeyUp = {()=>this._sliderOnMouseUp()}
			onTouchEnd = {()=>this._sliderOnMouseUp()}
		>

			<Tooltip title={sliderTooltip} orient="top">{sliderLabel}</Tooltip>
			<div className = 'relationship-vis-view__sliderContainer'>
				<Slider
					showTooltip
					value={this.state.slider}
					showBoundaryLabels
					className = "relationship-vis-view__slider"
					onChange={ (newValue) => {
						this.setState({ slider: newValue });
					}}
					small
					min = { NODESLIMIT_MIN } max = { NODESLIMIT_MAX } />
			</div>
		</div>);
	}

	_sliderOnMouseUp(){
		this.props.relationship.setNodesLimit(this.state.slider);
	}

	onResize() {
		if(this._isValidWidgetSize()) {
			this.widget.render(RENDER_MODE_CLIENT);
		}
	}

	// This is to avoid issue when When switching from Explore to other perspective (e.g Home) and switch back to Explore
	_isValidWidgetSize() {
		return this.widget && this.widget.domNode && this.widget.domNode.clientHeight > 0 && this.widget.domNode.clientWidth > 0;
	}

	_callback() {
		//TODO: Verify why this function is needed
	}

	/*
	 * Mouse Drop event handler
	 *
	 * Event handler for dragging columns from metadata tree and dropping them
	 * on the relationship widget.
	 */
	_handleDrop(dragObj) {
		this.setState({ 'dndHover': false });
		const itemIds = this._getDragItems(dragObj);
		if(!itemIds) {
			return;
		}
		const targetId = this.props.relationship.getCurrentTargetId();
		if (targetId) {
			if (this.nodesFound.length){
				this.nodesFound.forEach((nodeId) => {
					this.removeHighlight(nodeId, targetId);
				});
				this.widget.render('client');
			}
			if (this.nodesFound.length !== itemIds.length) {
				// some nodes are not found
				this._handleDragLeave();
				const targetModel = this.props.relationship.getTargetModel();
				if(targetModel){
					targetModel.setLastAddedFields(itemIds);
				}
			}
		}

	}

	/*Highlight the nodes if the dragged columns are already displayed in the diagram*/
	_handleDragEnter(dragObj) {
		this.nodesFound = [];
		const targetId = this.props.relationship.getCurrentTargetId();
		const itemIds = this._getDragItems(dragObj);
		if(targetId && itemIds){
			itemIds.forEach((itemId) => {
				if (this.addHighlight(itemId, targetId)) {
					this.nodesFound.push(itemId);
				}
			});

			if(this.nodesFound.length === itemIds.length){
				// all nodes are already displayed in the diagram, just call render to make highlight effective
				this.widget.render('client');
			}else{
				// at least 1 node is not displayed in the diagram, make the diagram container visible in waiting for looking up the new node
				this.setState({ 'dndHover': true });
				this.el.classList.add('relationship-vis-view__widget-parent-placeholder--dragEnter');
			}
		}
	}

	_handleDragLeave(){
		this.setState({ 'dndHover' : false });
		this.el.classList.remove('relationship-vis-view__widget-parent-placeholder--dragEnter');
	}

	_getDragItems(dragObj){
		if(dragObj && dragObj.data && dragObj.data.items){
			const items = dragObj.data.items;
			if(items.length){
				return items.map(item => item.idForExpression);
			}
		}
		return null;
	}

	_handleAccept(dragObj) {
		//TODO: We should only accept columns
		void(dragObj);
		return true;
	}

	_onClick(event) {
		const eventHandler = this._getEventHandler();
		return eventHandler && eventHandler.onClick(event);
	}

	_onContextMenu(event) {
		const eventHandler = this._getEventHandler();
		return eventHandler && eventHandler.onRightClick(event);
	}

	_onMouseOver(event) {
		const eventHandler = this._getEventHandler();
		return eventHandler && eventHandler.onMouseOver(event);
	}

	_onMouseOut(event) {
		const eventHandler = this._getEventHandler();

		return eventHandler && eventHandler.onMouseOut(event);
	}

	_onMouseEnter(){
		this.hintRef.current.classList.remove('starting-points-page__relationship__hint__hidden');
	}

	_onMouseLeave(){
		this.hintRef.current.classList.add('starting-points-page__relationship__hint__hidden');
	}

	_onTouchStart(event) {
		const eventHandler = this._getEventHandler();
		eventHandler && eventHandler.onTouchStart(event);
	}

	_onTouchEnd(event) {
		const eventHandler = this._getEventHandler();
		eventHandler && eventHandler.onTouchEnd(event);
	}

	_onKeyDown(event) {
		const eventHandler = this._getEventHandler();
		eventHandler && eventHandler.onKeyDown(event);
	}

	_getEventHandler() {
		return this.getEventHandler && this.getEventHandler();
	}
}

RelationshipWidgetView.propTypes = {
	relationship: PropTypes.object.isRequired,
	services: PropTypes.object,
	fields: PropTypes.object
};


export default RelationshipWidgetView ;



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/RelationshipWidgetView.js